import { IconHighlight } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { History, ArrowRight } from 'lucide-react';
import { studioLabelKey } from '../workspaceSelectorData.js';

/**
 * "Pick up where you left off" banner. The whole banner is one click target and
 * resumes the last-visited studio in the last workspace through the same
 * scoped-token switch the cards and chips use. Renders nothing when there is no
 * last-visited record (a first-ever login).
 *
 * Built as a single `<button>` rather than a `Card` with a stretched overlay
 * (the pattern the workspace cards use) because there is nothing else
 * interactive inside it — one real button is the simpler accessible answer.
 * `Card`'s surface classes would also have to be `!`-overridden away to get the
 * brand tint, as they are on WorkspaceCardFrame.
 */
function ResumeBanner({ record, disabled, busy, onResume }) {
  const { t } = useTranslation('core-shell');
  if (!record) return null;

  const detail = t('workspaceSelector.resumeDetail', {
    workspace: record.workspaceName,
    studio: t(studioLabelKey(record.studioId)),
    time: record.timeLabel,
  });

  return (
    <button
      type="button"
      disabled={disabled || busy}
      onClick={() => onResume(record.workspaceId, record.studioId)}
      className={[
        'group mb-4 flex w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left',
        'border-brand-500/25 bg-brand-500/10 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60',
        busy ? 'cursor-wait opacity-70' : '',
        // Mutually exclusive so two cursor utilities never both apply.
        !busy && disabled ? 'cursor-default' : '',
        !busy && !disabled ? 'hover:bg-brand-500/[0.16]' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* DS icon tile: carries its own light/dark pair, unlike the hand-rolled
          `bg-brand-500/20 text-brand-300` it replaces — that shade was tuned
          for dark only and washed out on the light surface. */}
      <IconHighlight icon={History} bgColor="Primary" iconColor="PrimaryAlt" size="medium" />

      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-content">
          {t('workspaceSelector.resumeTitle')}
        </span>
        <span className="block truncate text-xs text-content-muted">{detail}</span>
      </span>

      <ArrowRight
        size={16}
        className="flex-shrink-0 text-content-faint transition-transform group-hover:translate-x-0.5"
      />
    </button>
  );
}

export default ResumeBanner;
